import React from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import Slider from "react-slick";
import ImageCarousel from "src/components/ImageCarousel/ImageCarousel";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/**
 * ResponsiveImageGrid - Shows a carousel on mobile and a grid on desktop
 * @param {Array} images - Array of image objects with src and alt
 * @param {Object} gridConfig - Grid configuration (xs, sm, md, lg, etc.)
 * @param {Object} carouselConfig - Carousel configuration for mobile
 * @param {boolean} desktopPeekingCarousel - Desktop only: when true and more than 4 images, use a carousel (arrows/dots) showing 4 slides plus half of the next; swipe and drag enabled
 */
const ResponsiveImageGrid = ({
  images = [],
  gridConfig = { xs: 12, sm: 6, md: 3 },
  carouselConfig = {},
  className = "",
  desktopPeekingCarousel = false,
}) => {
  const defaultCarouselSettings = {
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 700,
    arrows: false,
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    ...carouselConfig,
  };

  const desktopPeekSliderSettings = {
    dots: true,
    arrows: true,
    infinite: carouselConfig.infinite !== false,
    speed: carouselConfig.speed ?? 500,
    slidesToShow: 5,
    slidesToScroll: 1,
    swipe: true,
    draggable: true,
    touchMove: true,
    lazyLoad: true,
    autoplay: carouselConfig.autoplay !== false,
    autoplaySpeed: carouselConfig.autoplaySpeed ?? 3000,
  };

  return (
    <Wrapper>
      {/* Mobile Carousel */}
      <MobileOnly>
        <ImageCarousel images={images} {...defaultCarouselSettings} />
      </MobileOnly>

      {/* Desktop: peeking carousel (>4 images)*/}
      <DesktopOnly>
        {desktopPeekingCarousel && images.length > 4 ? (
          <DesktopPeekCarouselWrapper className={className}>
            <Slider {...desktopPeekSliderSettings}>
              {images.map((image, index) => (
                <DesktopPeekSlide key={index}>
                  <ResponsiveImage
                    src={image.src}
                    alt={image.alt || `Image ${index + 1}`}
                  />
                </DesktopPeekSlide>
              ))}
            </Slider>
          </DesktopPeekCarouselWrapper>
        ) : (
          <Row className={className}>
            {images.map((image, index) => (
              <Col key={index} {...gridConfig}>
                <ResponsiveImage
                  src={image.src}
                  alt={image.alt || `Image ${index + 1}`}
                />
              </Col>
            ))}
          </Row>
        )}
      </DesktopOnly>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const MobileOnly = styled.div`
  display: block;

  @media (min-width: 768px) {
    display: none;
  }
`;

const DesktopOnly = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
  }
`;

const DesktopPeekCarouselWrapper = styled.div`
  width: 100%;
  position: relative;

  .slick-slider {
    width: 100%;
  }

  .slick-dotted.slick-slider {
    margin-bottom: 0;
  }

  .slick-list {
    overflow: hidden;
    cursor: grab;
  }

  .slick-list:active {
    cursor: grabbing;
  }

  .slick-track {
    cursor: grab;
    display: flex;
  }

  .slick-slide {
    outline: none;
    height: auto;

    > div {
      outline: none;
      height: 100%;
    }
  }

  .slick-prev,
  .slick-next {
    z-index: 1;
    width: 36px;
    height: 36px;
  }

  .slick-prev {
    left: 4px;
  }

  .slick-next {
    right: 4px;
  }

  .slick-prev:before,
  .slick-next:before {
    opacity: 0.9;
    font-size: 36px;
  }

  .slick-dots {
    position: relative;
    bottom: auto;
    left: auto;
    transform: none;

    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: fit-content;
    max-width: 100%;
    min-height: 35px;
    padding: 0 10px;
    margin: 12px auto 0;
    list-style: none;

    border-radius: 20px;
    background: rgba(0, 0, 0, 0.7);
    box-shadow:
      0 2px 4px 0 rgba(0, 0, 0, 0.4),
      0 1px 3px 0 rgba(0, 0, 0, 0.2);
  }

  .slick-dots li {
    margin: 0;
    width: auto;
    height: auto;
    padding: 0;
  }

  .slick-dots li button {
    width: 22px;
    height: 22px;
    padding: 0;
    position: relative;
  }

  .slick-dots li button:hover:before,
  .slick-dots li button:focus:before {
    opacity: 1;
  }

  .slick-dots li button:before {
    content: "";
    font-family: inherit;
    font-size: 0;
    line-height: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    margin: 0;
    padding: 0;
    opacity: 1;
    color: transparent;
    border-radius: 50%;
    background: var(--lightgrey);
    transform: translate(-50%, -50%);
    transition: all 0.3s ease;
  }

  .slick-dots li.slick-active button:before {
    width: 12px;
    height: 12px;
    background: var(--grey);
  }
`;

const DesktopPeekSlide = styled.div`
  padding: 0 6px;
  outline: none;
  box-sizing: border-box;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

export default ResponsiveImageGrid;
